import React, { useEffect, useState, useCallback } from "react";
import debounce from "lodash.debounce";
import ResourceCard from "./ResourceCard";
import ShimmerGrid from "../../revampComponents/ShimmerGrid";
import { getResources } from "../../crud/resources.crud";
import { errorToaster } from "../../helpers/utils";
import Pagination from "./Pagination";
import MobilePagination from "./MobilePagination";

const ResourceList = () => {
    const [resourceData, setResourceData] = useState({ resources: [], count: 0 });
    const [filteredResources, setFilteredResources] = useState([]);
    const [limit] = useState(9); // Set limit to 9 items per page
    const [resourceType, setResourceType] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    // Function to fetch all resources initially
    const fetchResources = async () => {
        setIsLoading(true);
        try {
            const res = await getResources();
            if (res.status === 200) {
                setResourceData(res.data);
                setFilteredResources(res.data.resources); // Initially, show all resources
            }
        } catch (err) {
            errorToaster("Something went wrong");
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch resources on component mount
    useEffect(() => {
        fetchResources();
    }, []);

    // Function to filter resources by search query
    const filterResources = (query) => {
        const filtered = resourceData.resources.filter((resource) =>
            resource.title.toLowerCase().includes(query.toLowerCase())
        );
        setFilteredResources(filtered);
        setCurrentPage(1); // Reset to first page on search
    };

    // Debounced function for handling search input
    const debouncedSearch = useCallback(
        debounce((value) => {
            filterResources(value);
        }, 300),
        [resourceData]
    );
    const scrollToTop = () => {
        window.scrollTo({
          top: 400,
          behavior: "smooth",
        });
      };

    // Handle search field change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
        debouncedSearch(value);
    };

    // Calculate total pages based on filtered resources length
    const totalPages = Math.ceil(filteredResources.length / limit);

    // Paginate filtered resources
    const paginatedResources = filteredResources.slice(
        (currentPage - 1) * limit,
        currentPage * limit
    );

    // Pagination handlers
    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
        setTimeout(() => { scrollToTop(); }, 50);

    };

    const handleNextClick = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
            setTimeout(() => { scrollToTop(); }, 50);

        }
    };

    const handlePreviousClick = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
            setTimeout(() => { scrollToTop(); }, 50);
        }
    };

    return (
        <div className="px-sm-5 px-3 py-5">
            <div className="resource-list-container mb-4">
                <h6 className="resource-list-title">
                    {filteredResources.length > 0
                        ? `${filteredResources.length} LEARNING RESOURCES`
                        : "0 Result"}
                </h6>
                <div className="search-bar">
                    <span className="search-icon">
                        <img
                            src="https://d2nnrpx7gk2my2.cloudfront.net/files/6706c2be839faa0008e452fb/searchmagnifyingglass.svg"
                            alt="search"
                        />
                    </span>
                    <input
                        type="text"
                        placeholder="Search Resources"
                        value={searchQuery}
                        onChange={handleSearchChange}
                    />
                </div>
            </div>

            <div className="row d-flex">
                {isLoading ? (
                    <ShimmerGrid count={9} />
                ) : paginatedResources.length === 0 ? (
                    <div className="no-resource-found">No resources found</div>
                ) : (
                    paginatedResources.map((resource) => (
                        <ResourceCard key={resource.id} resource={resource} />
                    ))
                )}
            </div>

            {filteredResources.length > 0 && totalPages > 1 && (
                <div>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageClick={handlePageClick}
                        onNextClick={handleNextClick}
                        onPreviousClick={handlePreviousClick}
                    />
                    <MobilePagination
                        totalPages={totalPages}
                        currentPage={currentPage}
                        onNextClick={handleNextClick}
                        onPreviousClick={handlePreviousClick}
                    />
                </div>
            )}

            {/* Additional Styles */}
            <style jsx>{`
                .resource-list-container {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                }

                .search-bar {
                    display: flex;
                    border: 1px solid #d6d6d6;
                    padding: 5px 30px 5px 10px;
                    border-radius: 2px;
                    gap: 5px;
                }

                .search-bar input {
                    border: none;
                    outline: none;
                    font-size: 16px;
                }

                .no-resource-found {
                    text-align: center;
                    padding: 20px;
                    color: #525252;
                }
                 @media (max-width: 576px) {
                 
                    .resource-list-container{
                        display:flex;
                        flex-direction:column;
                        align-items:flex-start;
                        gap:10px;
                    }
                    .search-bar{
                        width:100%;
                        margin-bottom:10px;
                    }
                 }
            `}</style>
        </div>
    );
};

export default ResourceList;